import { NGXLogger } from 'ngx-logger';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AppConstants } from '../commons/app-constants';
import { ToastMessagesService } from '../commons/services/toast-messages.service';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { OfflineLeaseService } from './offline-lease.service';
import { PushNotificationService } from './push-notification.service';
import { OutboxService } from '../offline/outbox.service';
import { NotificationSocketService } from '../notifications/notification-socket.service';
import { resetOfflineIdentityVerified, wipeReadCache } from '../offline/offline-db';
import { SyncEngineService, SyncRunOutcome } from '../offline/sync-engine.service';
import { NetworkStatusService } from './network-status.service';
import { isOfflineCapableNative } from '../commons/utils/platform';


@Injectable({
  providedIn: 'root',
})
export class LogoutService {
  private readonly logger = inject(NGXLogger);


  constructor(
    private http: HttpClient,
    private messageService: ToastMessagesService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private offlineLeaseService: OfflineLeaseService,
    private pushNotificationService: PushNotificationService,
    private outboxService: OutboxService,
    private notificationSocketService: NotificationSocketService,
    private syncEngineService: SyncEngineService,
    private networkStatusService: NetworkStatusService,
  ) { }

  /** How long the pre-logout sync may run before the user is asked "keep waiting / cancel logout". */
  private static readonly LOGOUT_SYNC_PATIENCE_MS = 60_000;

  /* logout() {
    const url = AppConstants.API_URL + "api/v1/logout";
    window.localStorage.clear();
    console.log(`Sign out url is ${url}`);
    return this.http.post(url, null)
      .subscribe(
        (success) => {
          window.localStorage.clear();
          this.messageService.showSuccessMessage('Logout Successful', 'You have been successfully logged out.');
          setTimeout(() => {
            
            this.router.navigate(['login']);;
          }, 1500);

        },
        (error: HttpErrorResponse) => {
          console.log(`Error is ${JSON.stringify(error)}`)
          this.messageService.showErrorMessage('Logout failed', 'Error while log out ')
        },
        () => {
          
         })
      ;

  }
 */

  logout(): Observable<boolean> {
    // A different user logging in on this device afterward would otherwise have any offline
    // work still queued here replayed under their own session — the write queue (outbox)
    // deliberately survives a normal logout so the SAME user recovers it after a forced
    // re-login (see wipeAllOfflineData()'s usage in AuthSessionGuard, which is the only place
    // that ever clears it, and only on a genuinely different user). Only interrupts the common
    // case (nothing pending) not at all — this resolves true immediately when the outbox is empty.
    return from(this.prepareLogout()).pipe(
      switchMap((proceed) => {
        if (!proceed) return of(false);

        const url = AppConstants.API_URL + "api/v1/user-logout";
        this.logger.debug('[LogoutService] Logout request started', { url });
        return this.http.post(url, null, { withCredentials: true }).pipe(
          tap((response) => this.logger.debug('[LogoutService] Logout request succeeded', response)),
          map(() => {
            this.clearClientSession('backend-success');
            return true;
          }),
          catchError((error: HttpErrorResponse) => {
            this.logger.warn('[LogoutService] Logout request failed', {
              status: error.status,
              message: error.message,
              error: error.error
            });
            if (error.status === 401 || error.status === 402 || error.status === 0) {
              this.clearClientSession(`backend-${error.status}`);
              return of(true);
            }
            return of(false);
          })
        );
      }),
    );
  }

  // Non-interactive forced logout — used by AuthSessionGuard when a session can no longer be trusted
  // (max-offline-days exceeded, or a device that has never completed a single successful sync).
  // Unlike logout(), this deliberately SKIPS the pre-logout sync (a guard mid-navigation can't block on
  // it) and wipes the offline READ cache — a forced reset means that data is no longer trustworthy.
  // OFFLINE_PLAN.md 1.7 (2026-09-29): it never deletes the write queue any more. The guard only calls
  // this when nothing is waiting (with changes waiting it shows the 15-day lock instead), so whatever is
  // left is refused items only — kept for the user to see in Sync Issues after logging back in.
  forceLogout(reason: string, loginQueryParams?: Record<string, string>): Observable<void> {
    const url = AppConstants.API_URL + "api/v1/user-logout";
    return this.http.post(url, null, { withCredentials: true }).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.warn('[LogoutService] forceLogout server notify failed (proceeding with local cleanup anyway)', {
          status: error.status,
          message: error.message,
        });
        return of(null);
      }),
      switchMap(() => from(wipeReadCache()).pipe(
        catchError((err) => {
          this.logger.warn('[LogoutService] forceLogout wipe failed', err);
          return of(undefined);
        }),
      )),
      tap(() => this.clearClientSession(`force-${reason}`, loginQueryParams)),
      map(() => undefined),
    );
  }

  // OFFLINE_PLAN.md 1.6 (2026-09-29) — every logout syncs first, so no work is ever left behind:
  //  - nothing waiting → log out as before;
  //  - changes waiting + offline → blocked (D1): connect first;
  //  - changes waiting + online → sync them now; still waiting afterwards (connection dropped) → blocked;
  //    login expired during the sync → the session-expired dialog takes over, and the sync continues by
  //    itself after the user logs back in (they then tap Logout again);
  //  - items the server refused → listed, "Delete and log out" (D3) or Cancel.
  // Web never queues anything offline, so this resolves true at once there.
  private async prepareLogout(): Promise<boolean> {
    if (!isOfflineCapableNative()) return true;
    let waiting = await this.outboxService.countUnsynced();
    if (waiting > 0) {
      if (!this.networkStatusService.isOnline) {
        await this.tell('Changes waiting to sync',
          `${this.changes(waiting)} waiting on this phone. Connect to the internet to sync, then log out.`);
        return false;
      }
      this.messageService.showInfoMessage('Syncing before logout', `Sending ${this.changes(waiting)}…`);
      const outcome = await this.syncBeforeLogout();
      if (!outcome || outcome.authExpired) return false;
      waiting = await this.outboxService.countUnsynced();
      if (waiting > 0) {
        await this.tell('Changes waiting to sync',
          `${this.changes(waiting)} could not be sent yet. Connect to the internet to sync, then log out.`);
        return false;
      }
    }
    const refused = await this.outboxService.getRejected();
    if (refused.length) {
      const list = refused.slice(0, 8)
        .map((r) => `• ${this.describe(r.opType)}${r.lastError ? ' — ' + this.escapeHtml(r.lastError) : ''}`)
        .join('<br>');
      const more = refused.length > 8 ? `<br>…and ${refused.length - 8} more` : '';
      const deleteThem = await this.ask('Some changes could not be saved',
        `The server refused ${this.changes(refused.length)}:<br>${list}${more}<br><br>Delete them and log out?`,
        'Delete and log out', 'Cancel');
      if (!deleteThem) return false;
      for (const entry of refused) {
        await this.syncEngineService.discardRejected(entry).catch(() => undefined);
      }
    }
    return true;
  }

  /** Runs the sync; after every minute without an end asks "keep waiting / cancel logout". null = cancelled. */
  private async syncBeforeLogout(): Promise<SyncRunOutcome | null> {
    const run = this.syncEngineService.syncNow(true); // the user asked to log out now — ignore retry waits
    for (;;) {
      const finished = await Promise.race([
        run.then((outcome) => ({ outcome })),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), LogoutService.LOGOUT_SYNC_PATIENCE_MS)),
      ]);
      if (finished) return finished.outcome;
      const keepWaiting = await this.ask('Still syncing',
        'Your changes are still being sent. Keep waiting, or cancel the logout? Nothing is lost if you cancel.',
        'Keep waiting', 'Cancel logout');
      if (!keepWaiting) return null;
    }
  }

  /** The confirm dialog renders its message as HTML — server error text must not be read as markup. */
  private escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }

  private changes(n: number): string {
    return n === 1 ? '1 change' : `${n} changes`;
  }

  private describe(opType: string): string {
    switch (opType) {
      case 'CREATE_TU': return 'New transport unit';
      case 'SAVE_TALLY_DETAILS': return 'Tally rows';
      case 'DELETE_TALLY_DETAILS': return 'Deleted tally rows';
      case 'CREATE_BP': return 'New business partner';
      case 'CREATE_PRODUCT': return 'New product';
      case 'CREATE_LOADING_SITE': return 'New loading site';
      case 'SAVE_TALLY_SETTINGS':
      case 'UPDATE_GRID_STYLE': return 'Tally settings';
      case 'UPLOAD_DOCUMENT': return 'Document';
      case 'UPLOAD_PRODUCT_PHOTO': return 'Product photo';
      case 'CREATE_PROCESSING_RUN': return 'Custom-made request';
      default: return 'Change';
    }
  }

  private tell(header: string, message: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.confirmationService.confirm({
        key: 'unsyncedLogoutConfirm',
        header,
        icon: 'pi pi-cloud-upload',
        message,
        acceptLabel: 'OK',
        rejectVisible: false,
        accept: () => resolve(),
        reject: () => resolve(),
      });
    });
  }

  private ask(header: string, message: string, acceptLabel: string, rejectLabel: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.confirmationService.confirm({
        key: 'unsyncedLogoutConfirm',
        header,
        icon: 'pi pi-exclamation-triangle',
        message,
        acceptLabel,
        rejectLabel,
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  clearClientSession(reason: string, extraQueryParams?: Record<string, string>): void {
    this.logger.debug('[LogoutService] Clearing client session', { reason });
    // The next login must pass the different-user check again before anything syncs (OFFLINE_PLAN.md).
    resetOfflineIdentityVerified();
    const tourEntries = this.extractTourFlags();
    // Must survive localStorage.clear() below — the NEXT login compares against this to
    // detect a different user on this device (see AuthSessionGuard.wipeOfflineDataIfDifferentUser).
    const lastDeviceUserEmail = localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
    // Same rationale, and it must be preserved TOGETHER with the email above: losing only the
    // tenant marker would make the next login look like an unverifiable legacy cache and force
    // a pointless full read-cache wipe (cold resync) on every ordinary logout/login cycle.
    const lastDeviceTenantId = localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID);
    localStorage.clear();
    sessionStorage.clear();
    tourEntries.forEach(([k, v]) => localStorage.setItem(k, v));
    if (lastDeviceUserEmail) {
      localStorage.setItem(AppConstants.LAST_DEVICE_USER_EMAIL, lastDeviceUserEmail);
    }
    if (lastDeviceTenantId) {
      localStorage.setItem(AppConstants.LAST_DEVICE_TENANT_ID, lastDeviceTenantId);
    }
    Sentry.setUser(null);
    // The offline READ cache is intentionally left in place here — a logout by the same user
    // who then logs back in should keep their cached data so the next online-transition sync
    // (OfflinePrefetchService) can refresh it in place instead of starting from a cold cache.
    // It's only ever cleared by AuthSessionGuard.wipeOfflineDataIfDifferentUser, and only when a
    // genuinely different email logs in on this device next. Fire-and-forget — don't block
    // navigation on IndexedDB/secure-storage teardown for what IS still cleared here.
    this.offlineLeaseService.clear().catch((err) => this.logger.warn('[LogoutService] Failed to clear offline lease', err));
    this.pushNotificationService.unregisterCurrentToken().catch((err) => this.logger.warn('[LogoutService] Failed to unregister push token', err));
    // Without this, the STOMP client's reconnectDelay retries forever against the now-invalid
    // session, and each retry's onWebSocketClose would keep resurrecting the unread-count
    // fallback poll from the login screen.
    this.notificationSocketService.shutdown();
    this.router.navigate(['/login'], { queryParams: { forceLogin: 'true', ...extraQueryParams } });
  }

  private extractTourFlags(): [string, string][] {
    const entries: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('tms_tour_')) entries.push([k, localStorage.getItem(k)!]);
    }
    return entries;
  }
  
  

}
